import type { LoroDoc } from "loro-crdt";
import { useTranslation } from "react-i18next";
import { setProp } from "../core/ops";
import type { Task } from "../core/schema";
import { Checkbox, Chip, Select } from "../ui";

export interface Person {
  id: string;
  label: string;
  color: string | null;
}

/**
 * *With* and *Remind me when we meet* (P2, circle mode): the people a task is about — not an owner or assignee,
 * it never limits who sees or does the task — and whether to be reminded of it when I am together with one
 * of them.
 */
export function MeetEditor({
  task,
  ro,
  write,
  people,
  me,
}: {
  task: Task;
  ro: boolean;
  write(fn: (doc: LoroDoc) => void): void;
  people: Person[];
  me: string | null;
}) {
  const { t } = useTranslation();
  const chosen = Array.isArray(task.props.with) ? (task.props.with as string[]) : [];
  const on = task.props.whenWeMeet === true;
  const nameOf = (id: string) => people.find((p) => p.id === id);
  const set = (next: string[]) =>
    write((doc) => {
      setProp(doc, task.id, "with", next.length ? next : null);
      if (!next.length) setProp(doc, task.id, "whenWeMeet", null);
    });
  const addable = people.filter((p) => p.id !== me && !chosen.includes(p.id));
  return (
    <div className="flex flex-col gap-2" data-testid="meet">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[13px]">{t("meet.with")}</span>
        {chosen.map((id) => (
          <Chip
            key={id}
            tone={nameOf(id)?.color}
            removeLabel={t("meet.remove", { name: nameOf(id)?.label ?? "" })}
            onRemove={ro ? undefined : () => set(chosen.filter((x) => x !== id))}
          >
            {nameOf(id)?.label ?? t("meet.someone")}
          </Chip>
        ))}
        {!ro && addable.length > 0 && (
          <Select
            compact
            value=""
            aria-label={t("meet.add")}
            onChange={(e) => e.target.value && set([...chosen, e.target.value])}
            data-testid="meet-add"
          >
            <option value="">{t("meet.add")}</option>
            {addable.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </Select>
        )}
      </div>
      <label className="flex items-center gap-2 text-[13px]">
        <Checkbox
          checked={on}
          disabled={ro || chosen.length === 0}
          label={t("meet.remind")}
          onChange={(v) => write((doc) => setProp(doc, task.id, "whenWeMeet", v ? true : null))}
        />
        <span data-testid="meet-remind">{t("meet.remind")}</span>
      </label>
      <p className="text-[12px] text-neutral-500">{t("meet.hint")}</p>
    </div>
  );
}
